import { THREE } from "../config/ThreeConfig.js";

const RETICLE_AIM_DISTANCE = 520;

export function getRendererViewport(renderer) {
  const width = renderer?.domElement?.clientWidth || globalThis.window?.innerWidth || 1;
  const height = renderer?.domElement?.clientHeight || globalThis.window?.innerHeight || 1;
  return { width: Math.max(1, width), height: Math.max(1, height) };
}

function getPlayerWorldPosition(player) {
  if (!player?.object3D) return null;

  const shipWorld = new THREE.Vector3();
  if (typeof player.object3D.getWorldPosition === "function") {
    player.object3D.getWorldPosition(shipWorld);
  } else if (player.object3D.position?.isVector3) {
    shipWorld.copy(player.object3D.position);
  } else {
    return null;
  }
  return shipWorld;
}

function getPlayerForward(player) {
  if (!player?.object3D) return null;
  const q = new THREE.Quaternion();
  if (typeof player.object3D.getWorldQuaternion === "function") {
    player.object3D.getWorldQuaternion(q);
  } else if (player.object3D.quaternion?.isQuaternion) {
    q.copy(player.object3D.quaternion);
  } else {
    return null;
  }

  const fwd = new THREE.Vector3(0, 0, -1).applyQuaternion(q);
  if (!Number.isFinite(fwd.x) || !Number.isFinite(fwd.y) || !Number.isFinite(fwd.z) || fwd.lengthSq() < 1e-8) {
    return null;
  }
  return fwd.normalize();
}

export function getShipForwardAimPoint(player, aimDistance = RETICLE_AIM_DISTANCE) {
  const shipWorld = getPlayerWorldPosition(player);
  const forward = getPlayerForward(player);
  if (!shipWorld || !forward) return null;

  const requested = Number(player?.reticleDistance);
  const distance = Math.max(160, Number.isFinite(requested) ? requested : Number(aimDistance) || RETICLE_AIM_DISTANCE);
  return shipWorld.clone().add(forward.clone().multiplyScalar(distance));
}

export function getStarFoxReticleScreenPos(player, camera, renderer, mouseX = 0, mouseY = 0) {
  if (!player?.object3D || !camera) return null;

  const { width, height } = getRendererViewport(renderer);
  const shipWorld = getPlayerWorldPosition(player);
  if (!shipWorld) return null;

  const shipClip = shipWorld.clone().project(camera);
  if (!Number.isFinite(shipClip.x) || !Number.isFinite(shipClip.y) || shipClip.z > 1) return null;

  const shipScreenX = (shipClip.x * 0.5 + 0.5) * width;
  const shipScreenY = (-shipClip.y * 0.5 + 0.5) * height;

  if (!Number.isFinite(shipScreenX) || !Number.isFinite(shipScreenY)) return null;

  const rawX = Number(mouseX);
  const rawY = Number(mouseY);
  const aimX = Number.isFinite(rawX) && rawX > 0 ? rawX : width * 0.5;
  const aimY = Number.isFinite(rawY) && rawY > 0 ? rawY : height * 0.45;

  // Desktop aiming must feel like the mouse owns the reticle. Earlier builds
  // tethered the crosshair to the ship's projected screen position, which made
  // the reticle drift when the ship moved and made aiming feel out of sync.
  const marginX = width * 0.025;
  const marginY = height * 0.035;
  const screenX = THREE.MathUtils.clamp(aimX, marginX, width - marginX);
  const screenY = THREE.MathUtils.clamp(aimY, marginY, height - marginY);

  return {
    x: screenX,
    y: screenY,
    width,
    height,
    shipX: shipScreenX,
    shipY: shipScreenY,
    ndcX: (screenX / width) * 2 - 1,
    ndcY: -((screenY / height) * 2 - 1),
  };
}

export function getReticleAimPoint(player, camera, renderer, aimDistance = RETICLE_AIM_DISTANCE, mouseX = 0, mouseY = 0) {
  const screen = getStarFoxReticleScreenPos(player, camera, renderer, mouseX, mouseY);
  if (!screen || !camera) return null;

  const raycaster = new THREE.Raycaster();
  raycaster.setFromCamera({ x: screen.ndcX, y: screen.ndcY }, camera);
  const distance = Math.max(120, Number(aimDistance) || RETICLE_AIM_DISTANCE);
  return raycaster.ray.origin.clone().add(raycaster.ray.direction.clone().multiplyScalar(distance));
}

export function getAimDirectionThroughReticle(origin, player, camera, renderer, mouseX = 0, mouseY = 0) {
  if (!origin?.isVector3 || !camera) return null;

  const aimPoint = getReticleAimPoint(player, camera, renderer, RETICLE_AIM_DISTANCE, mouseX, mouseY);
  if (!aimPoint) return null;
  const direction = aimPoint.clone().sub(origin);

  if (
    direction.lengthSq() < 1e-6 ||
    !Number.isFinite(direction.x) ||
    !Number.isFinite(direction.y) ||
    !Number.isFinite(direction.z)
  ) {
    return null;
  }

  return direction.normalize();
}
